import {addDays,cents,type Row} from './finance';
import {liabilityKinds} from './accounts';

type Account=Row<'accounts'>;type Tx=Row<'transactions'>;type Valuation=Row<'account_valuations'>;
/** HANDOFF §16's groups, in the order they are listed. */
export const groups=[
 {key:'cash',label:'Cash',kinds:['checking','savings','cash']},{key:'investments',label:'Investments',kinds:['investment','crypto']},{key:'retirement',label:'Retirement',kinds:['retirement']},
 {key:'property',label:'Property',kinds:['property']},{key:'other_assets',label:'Other assets',kinds:['other_asset']},
 {key:'cards',label:'Credit cards',kinds:['credit']},{key:'mortgage',label:'Mortgage',kinds:['mortgage']},{key:'loans',label:'Loans',kinds:['personal_loan','auto_loan','student_loan']},{key:'other_liabilities',label:'Other liabilities',kinds:['other_liability']},
] as const;
export type GroupKey=typeof groups[number]['key'];
export const groupOf=(kind:string):GroupKey=>groups.find(g=>(g.kinds as readonly string[]).includes(kind))?.key??'other_assets';
/** Kinds whose worth is usually set by a valuation rather than the ledger. */
export const valuedKinds=new Set(['property','other_asset','investment','retirement','crypto']);

/**
 * An account's value on a date, in cents (liabilities as a positive amount owed). With a valuation
 * on or before the date, it is the latest valuation plus money moved in or out since; otherwise
 * the opening balance plus the ledger up to the date. Pending activity never counts.
 */
export function makeValuer(accounts:Account[],tx:Tx[],valuations:Valuation[]){
 const byAccount=new Map<string,{date:string;amount:number}[]>();
 for(const t of tx){if(t.status==='pending'||t.deleted_at)continue;const list=byAccount.get(t.account_id)??[];list.push({date:t.transaction_date,amount:cents(t.amount)});byAccount.set(t.account_id,list)}
 for(const list of byAccount.values())list.sort((a,b)=>a.date.localeCompare(b.date));
 const vals=new Map<string,Valuation[]>();for(const v of valuations){const list=vals.get(v.account_id)??[];list.push(v);vals.set(v.account_id,list)}
 for(const list of vals.values())list.sort((a,b)=>a.valued_on.localeCompare(b.valued_on));
 const sum=(id:string,after:string|null,through:string)=>(byAccount.get(id)??[]).reduce((s,t)=>t.date<=through&&(after===null||t.date>after)?s+t.amount:s,0);
 return (a:Account,date:string)=>{
  const sign=liabilityKinds.has(a.kind)?-1:1;const v=(vals.get(a.id)??[]).filter(x=>x.valued_on<=date).at(-1);
  return v?cents(v.value)+sign*sum(a.id,v.valued_on,date):cents(a.opening_balance)+sign*sum(a.id,null,date);
 };
}

export type Point={date:string;assets:number;liabilities:number;net:number};
/** Net worth on each date from the accounts that count toward it. */
export function netWorthSeries(accounts:Account[],tx:Tx[],valuations:Valuation[],dates:string[]):Point[]{
 const value=makeValuer(accounts,tx,valuations);const counted=accounts.filter(a=>!a.excluded_from_net_worth&&!a.is_archived);
 return dates.map(date=>{let assets=0,liabilities=0;for(const a of counted){const v=value(a,date);if(liabilityKinds.has(a.kind))liabilities+=v;else assets+=v}return {date,assets,liabilities,net:assets-liabilities}});
}

export const ranges=[{key:'1M',days:30},{key:'3M',days:91},{key:'6M',days:182},{key:'YTD',days:0},{key:'1Y',days:365},{key:'2Y',days:730},{key:'5Y',days:1826}] as const;
export type RangeKey=typeof ranges[number]['key'];
/** Dates to plot for a range: daily up to six months, weekly beyond, always ending today. */
export function rangeDates(range:RangeKey,today:string){
 const r=ranges.find(x=>x.key===range)!;const start=range==='YTD'?`${today.slice(0,4)}-01-01`:addDays(today,-r.days);
 const step=r.days>200?7:1;const out:string[]=[];for(let d=today;d>=start;d=addDays(d,-step))out.unshift(d);
 if(out[0]!==start)out.unshift(start);return out;
}

/**
 * What moved net worth between two dates: money saved or borrowed (the ledger) against changes in
 * value (valuations of homes, cars and investments beyond the money put in or taken out). A
 * valued account's change is its value change; its contributions are the ledger activity in it.
 */
export function explainChange(accounts:Account[],tx:Tx[],valuations:Valuation[],from:string,to:string){
 const value=makeValuer(accounts,tx,valuations);let total=0,market=0;
 for(const a of accounts.filter(x=>!x.excluded_from_net_worth&&!x.is_archived)){
  const sign=liabilityKinds.has(a.kind)?-1:1;const change=sign*(value(a,to)-value(a,from));total+=change;
  if(valuations.some(v=>v.account_id===a.id)){const moved=tx.filter(t=>t.account_id===a.id&&t.status!=='pending'&&!t.deleted_at&&t.transaction_date>from&&t.transaction_date<=to).reduce((s,t)=>s+cents(t.amount),0);market+=change-moved}
 }
 return {total,market,contributions:total-market};
}

/** Latest value of each counted account, grouped, for the breakdown. */
export function breakdown(accounts:Account[],tx:Tx[],valuations:Valuation[],date:string){
 const value=makeValuer(accounts,tx,valuations);
 return groups.map(g=>{const rows=accounts.filter(a=>!a.is_archived&&(g.kinds as readonly string[]).includes(a.kind)).map(a=>({account:a,value:value(a,date),counted:!a.excluded_from_net_worth}));
  return {...g,liability:liabilityKinds.has(g.kinds[0]),rows,total:rows.filter(r=>r.counted).reduce((s,r)=>s+r.value,0)}}).filter(g=>g.rows.length);
}
